
import { createSlice, createSelector } from '@reduxjs/toolkit';
import { logout } from "../common/reducers/initContext";
import { DefaultFailureAction, dataHookFactory, initMapFactory } from '../common/reducers/utilities';
import { ensureArray } from '../common/utils/arrayUtilities';
import { createEagerSelector, eagerMetaFactory } from '../common/reducers/pendingUpdateSlice';
import { nowISOString } from '../common/utils/dateUtils';
import { aggregatingFetch } from '../common/hooks/networkHook';

const compareNote = (n1, n2) => n1.id === n2.id;

const jobNoteSlice = createSlice({
    name: 'jobNote',
    initialState: {
        data: {}
    },
    reducers: {
        set: (state, action) => {
            state.data = action.payload;
        },
        addOrUpdate: (state, action) => {
            for (const note of ensureArray(action.payload)) {
                const jobid = note.jobid?.toString();
                if (state.data[jobid]) {
                    let idx = state.data[jobid].findIndex(a => compareNote(a, note));
                    if (idx >= 0) {
                        state.data[jobid].splice(idx, 1, note);
                    } else {
                        state.data[jobid].push(note);
                    }
                } else {
                    state.data[jobid] = [note];
                }
            }
        },
        remove: (state, action) => {
            for (const note of ensureArray(action.payload)) {
                const jobid = note.jobid?.toString();
                if (state.data[jobid]) {
                    let idx = state.data[jobid].findIndex(a => compareNote(a, note));
                    if (idx >= 0) {
                        state.data[jobid].splice(idx, 1);
                    }
                }
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => jobNoteSlice.getInitialState());
    }
});

// #region network operations
const visualizeFailure = verb => DefaultFailureAction(verb, 'jobNotes');
const eagerMeta = eagerMetaFactory(jobNoteSlice.name);
export const fetchJobNotes = aggregatingFetch(
    jobid => network => network.post('/api/v1/notesByJobId', ensureArray(jobid), jobNoteSlice.actions.set),
    jobids => network => network.post('/api/v1/notesByJobId', jobids, jobNoteSlice.actions.set)
);

export const createNote = (network, note) => network.post('/api/v1/jobnote', { id: crypto.randomUUID(), ...note, timestamp: note.timestamp ?? nowISOString(), autogenerated: note.autogenerated === true || note.autogenerated === '1' || note.autogenerated === 1 ? 1 : 0 }, jobNoteSlice.actions.addOrUpdate, { ...eagerMeta(note.id, { fk: note.jobid }), failure: visualizeFailure('create') });
export const strikeNote = (network, note) => network.put('/api/v1/jobnote', note, jobNoteSlice.actions.addOrUpdate, { ...eagerMeta(note.id, { fk: note.jobid }), failure: visualizeFailure('update') });
// #endregion

// #region selectors
const selectNote = state => state.jobNote.data;
const eagerSelector = createEagerSelector(jobNoteSlice.name, { mode: 'mapToArray' });
// we now make a reselect to memoize a select function parameterized by jobid and expose that factory through a convenience function
const selectNoteByJobID = eagerSelector(createSelector([selectNote, (note, jobid) => jobid], (note, jobid) => {
    return note[jobid?.toString()] ?? []
}));
const selectJobNote = jobid => state => selectNoteByJobID(state, jobid);

const selectAggregateJobNotes = jobids => state => {
    let result = {}
    for (const jobid of jobids) {
        result[jobid] = selectNoteByJobID(state, jobid)
    }
    return result;
}
// #endregion

// #region listeners
const listen = {
    jobNoteCreated: (note, { dispatch }) => dispatch(jobNoteSlice.actions.addOrUpdate(note)),
    jobNoteStruck: (note, { dispatch }) => dispatch(jobNoteSlice.actions.addOrUpdate(note))
}
// #endregion

const options = { cleanup: { root: 'jobNote', all: state => Object.values(state.jobNote.data).flat(), removeAll: jobNoteSlice.actions.remove, remove: jobNoteSlice.actions.remove }}
export const [useJobNotes,realizeJobNotes] = dataHookFactory(selectJobNote, initMapFactory(fetchJobNotes, listen), options);
export const [useAggregateJobNotes, realizeAggregateJobNotes] = dataHookFactory(selectAggregateJobNotes, initMapFactory(fetchJobNotes, listen), options);

const reducerDetails = { name: jobNoteSlice.name, reducer: jobNoteSlice.reducer };
export default reducerDetails;